import React, { useState } from 'react';
import * as Sentry from "@sentry/react";
import { createPortal } from 'react-dom';
import { ShieldAlert, X } from 'lucide-react';
import { reportPost, reportComment } from '../../services/api';
import { toast } from 'react-toastify';
import { CommunityError } from "../../errors/sentryErrors";
import { trackEvent } from '../../utils/analytics';

const REPORT_REASONS = [
  { value: 'spam', label: 'Spam' },
  { value: 'low_quality', label: 'Low Quality' },
  { value: 'offensive', label: 'Offensive Content' },
  { value: 'misleading', label: 'Misleading Information' },
  { value: 'personal_info', label: 'Contains Personal Information' },
  { value: 'copyright', label: 'Copyright Violation' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'other', label: 'Other' },
];

const ReportModal = ({ isOpen, onClose, targetId, targetType }) => {
  const [reason, setReason] = useState('spam');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason) return;
    
    setLoading(true);
    setError(null);
    try {
      if (targetType === 'post') {
        await reportPost(targetId, reason, note);
      } else {
        await reportComment(targetId, reason, note);
      }
      trackEvent('report_submitted', { target_type: targetType, target_id: targetId, reason });
      toast.success('Thanks for helping keep the community safe.', { icon: '🛡️' });
      onClose();
      // Reset form
      setReason('spam');
      setNote('');
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
      });
      if (err.status === 429) {
        toast.error('You\'ve reached today\'s reporting limit. Try again tomorrow.');
      } else if (err.status === 400 && err.issues) {
        setError(err.issues[0]?.message || 'Validation error');
      } else {
        toast.error('Failed to submit report. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-opacity" onClick={e => { e.preventDefault(); e.stopPropagation(); onClose(); }}>
      <div 
        className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2 text-red-600">
            <ShieldAlert size={20} />
            <h3 className="font-bold text-lg text-gray-900">Report Content</h3>
          </div>
          <button onClick={e => { e.preventDefault(); e.stopPropagation(); onClose(); }} className="p-1 hover:bg-gray-100 rounded-full text-gray-500 transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5">
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Reason for reporting</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full bg-gray-50 border border-gray-200 text-gray-700 py-2.5 px-3 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              required
            >
              {REPORT_REASONS.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <div className="flex justify-between mb-1">
              <label className="block text-sm font-semibold text-gray-700">Additional Notes <span className="text-gray-400 font-normal">(Optional)</span></label>
              <span className={`text-xs ${note.length > 950 ? 'text-red-500' : 'text-gray-400'}`}>
                {note.length}/1000
              </span>
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={1000}
              rows={4}
              className={`w-full bg-gray-50 border ${error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-200 focus:ring-blue-500'} text-gray-700 py-2.5 px-3 rounded-xl text-sm focus:outline-none focus:ring-1 resize-none`}
              placeholder="Provide any extra context..."
            />
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
          </div>

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={e => { e.preventDefault(); e.stopPropagation(); onClose(); }}
              disabled={loading}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              Submit Report
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ReportModal;
